/**
 * The Claim detail rows, fetched only when that page opens.
 *
 * A separate artefact from the bundle (see api/claims): ~400K claims, columnar and
 * dictionary-encoded, scoped server-side to the people the caller may see. Kept
 * columnar in the browser too — as objects the same rows cost several hundred MB.
 */
import { useCachedDetail } from './detailFetch'

export interface ClaimDetailData {
  version: string
  generatedAt: string
  n: number
  dict: {
    e: string[]
    dept: string[]
    f: string[]
    s: string[]
    p: string[]
    pn: string[]
    d: string[]
    st: string[]
    ty: string[]
    /** Service type. Optional: files built before it was added lack it. */
    sv?: string[]
    /**
     * The patient, "Last, First"; '' where Medevolve has no name (review #69). PHI:
     * /api/claims rebuilds it from the caller's own rows. Absent until the build can
     * read Medevolve's claim worklist.
     */
    pt?: string[]
    /** Why a claim weighs what it does, by `cols.wr`. With `cols.w`. */
    wr?: string[]
    /** The department the model credits the claim to, by `cols.cb` (docs/RULES.md). */
    cb?: string[]
    /**
     * The owner's home department in the claim's month, by `cols.wd`: the Overview's
     * department breakdown key (build_bundle.py, home_department). Absent before it.
     */
    wd?: string[]
    /**
     * The month a claim resolved in ('YYYY-MM'), by `cols.rm`; '' unless it is resolved
     * in a month its owner was paid (build_bundle.py claim_detail, review #397). Absent
     * on files built before it.
     */
    rm?: string[]
    /**
     * How the claim reached its owner (reviews #394, #405; src/data/attribution.ts):
     * the predicted worklist, the predicted person, the rule that credited it, and its
     * month-end trail through the open pile (JSON). Absent before the 2026-10-12 build.
     */
    pw?: string[]
    pu?: string[]
    ab?: string[]
    ot?: string[]
    /**
     * What Rand's period table (calc.predicted_worklist_periods) expected at billing, as
     * JSON: dates, priority, lists matched, facility coverage, and the period's person
     * where it is not `pu` (src/data/attribution.ts parseExpected). Absent before the
     * 2026-10-15 build or without the PredictedWorklistPeriods shortcut.
     */
    ep?: string[]
    /**
     * The model's own reason (calc.claim_attribution): its AttrSource, the co-insurance
     * outcome (CoinsRule) and the primary claim's id. Absent before the 2026-10-14 build.
     */
    as?: string[]
    cr?: string[]
    cp?: string[]
  }
  cols: {
    e: number[]
    dept: number[]
    f: number[]
    s: number[]
    p: number[]
    pn: number[]
    d: number[]
    st: number[]
    ty: number[]
    sv?: number[]
    pt?: number[]
    v: string[]
    lab: number[]
    chg: (number | null)[]
    rem: (number | null)[]
    ie: (0 | 1 | null)[]
    /**
     * Each claim's weight under the active claim rules (docs/RULES.md), added by
     * /api/claims. Absent when the rules could not be read (`weights: 'unavailable'`).
     */
    w?: number[]
    wr?: number[]
    /**
     * The model's own columns, where the pipeline writes them (docs/RULES.md, "Who
     * applies the rules"): credited department, included in the count, weight.
     */
    cb?: number[]
    ic?: (0 | 1 | null)[]
    cw?: (number | null)[]
    wd?: number[]
    rm?: number[]
    /**
     * 1 on a claim that is on the resolved basis only: resolved in a month its owner
     * was paid, but billed outside the period or in a month they were not. Every
     * billed-basis view and sum leaves it out (`billedBasis`). 0 otherwise.
     */
    xb?: (0 | 1)[]
    pw?: number[]
    pu?: number[]
    ab?: number[]
    ot?: number[]
    ep?: number[]
    as?: number[]
    cr?: number[]
    cp?: number[]
    /** The predicted worklist's id, for the Worklist assignments link; null for none. */
    pwk?: (number | null)[]
    /** The facility's Medevolve id (the ClaimID's prefix): matches assignments.json `fid`. */
    fid?: (number | null)[]
  }
  /** When the model read the rules (ISO); absent until it weighs claims. */
  rulesVersion?: string
  /** The rule set's tag, or 'unavailable'. Absent from an older API. */
  weights?: string
}

const CLAIMS_URL = (import.meta.env.VITE_CLAIMS_URL as string | undefined) ?? '/api/claims'

/** Shown from the browser's copy at once on a return visit; see detailFetch.ts. */
export function useClaimDetail(
  enabled = true,
): { data: ClaimDetailData | null; error: string | null } {
  return useCachedDetail<ClaimDetailData>(
    'claims',
    CLAIMS_URL,
    "Claim rows aren't available yet.",
    enabled,
  )
}
